import { NmLoader } from '../components/NmLoader';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline, IoSettingsOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { forms, metricsApi } from '../services/api';
import { HealthDocumentUploadSheet } from '../components/health/HealthDocumentUploadSheet';
import { NmButton } from '../components/NmButton';
import { NmInput } from '../components/NmInput';
import { useConfirm } from '../components/NmConfirmDialog';
import { formatIsoDateLabel } from '../i18n/formatters';
import { presentHealthMetricName } from '../utils/healthMetricPresentation';
import { displayDigits } from '../utils/displayDigits';
import { buildMetricFormEntries, formatMetricSubmitValue, isMetricEntryValid, type MetricFormEntry } from './metricFormHelpers';
import './metric-form.css';

interface MetricFormModalProps { date: string; userId: number; uniqueCode?: string | null; onSuccess: () => void; onClose: () => void; }

export function MetricFormModal({ date, userId, uniqueCode, onSuccess, onClose }: MetricFormModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const dialog = useRef<HTMLDialogElement>(null);
  const initialValues = useRef('[]');
  const submittingRef = useRef(false);
  const isEdit = Boolean(uniqueCode);
  const [entries, setEntries] = useState<MetricFormEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invalidIds, setInvalidIds] = useState<number[]>([]);
  const [healthDocOpen, setHealthDocOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [retry, setRetry] = useState(0);
  const title = isEdit ? t('metricForm.titleEdit') : t('metricForm.title');

  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (healthDocOpen || confirming) { if (node.open) node.close(); }
    else if (!node.open) node.showModal();
    return () => { if (node.open) node.close(); };
  }, [healthDocOpen, confirming]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    void (async () => {
      try {
        const res = isEdit && uniqueCode ? await forms.getMetricByUniqueCode(userId, uniqueCode) : await forms.getUserMetrics(userId);
        let metrics = res.data.data?.metrics;
        if (!Array.isArray(metrics)) throw new Error('Invalid metrics response');
        if (!isEdit) {
          try {
            const types = (await metricsApi.listTypes()).data.data?.metrics ?? [];
            const weight = types.find(type => type.name.toLowerCase() === 'weight');
            if (weight && !metrics.some(metric => metric.metric_type_id === weight.id)) metrics = [{ metric_type_id: weight.id, name: weight.name, data_type: 'number' }, ...metrics];
          } catch { /* Selected fields remain usable if the optional catalogue fails. */ }
        }
        if (!cancelled) {
          const next = buildMetricFormEntries(metrics);
          initialValues.current = JSON.stringify(next.map(row => row.value));
          setEntries(next);
        }
      } catch { if (!cancelled) setError(t('metricForm.error.loadFailed')); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isEdit, uniqueCode, userId, t, retry]);

  async function leave(action: () => void) {
    if (submittingRef.current || confirming) return;
    if (JSON.stringify(entries.map(row => row.value)) !== initialValues.current && !loading) {
      setConfirming(true);
      const discard = await confirm({ title:t('metricForm.unsavedTitle'), message:t('metricForm.unsavedMessage'), confirmLabel:t('metricForm.discard'), variant:'default' });
      setConfirming(false);
      if (!discard) return;
    }
    action();
  }
  function close() { void leave(onClose); }
  function update(index: number, value: string) {
    setEntries(rows => rows.map((row, i) => i === index ? { ...row, value, selectedItemId: Number(value) || '' } : row));
    setInvalidIds([]); setError('');
  }
  const filled = entries.filter(entry => entry.value.trim() !== '');
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (submittingRef.current || loading) return;
    if (!filled.length) { setError(t('metricForm.error.atLeastOne')); return; }
    const invalid = filled.filter(entry => !isMetricEntryValid(entry)).map(entry => entry.metric.metric_type_id);
    setInvalidIds(invalid);
    if (invalid.length) { setError(t('metricForm.error.invalid')); return; }
    submittingRef.current = true; setSubmitting(true); setError('');
    try {
      if (isEdit && uniqueCode) {
        await forms.updateMetric(userId, uniqueCode, filled.map(entry => ({ metric_type_id: entry.metric.metric_type_id, value: formatMetricSubmitValue(entry), user_metric_data_id: entry.userMetricDataId ?? null })));
      } else {
        const now = new Date();
        const time = [now.getHours(), now.getMinutes(), now.getSeconds()].map(value => String(value).padStart(2, '0')).join(':');
        await forms.createMetric(userId, `${date} ${time}`, filled.map(entry => ({ metric_type_id: entry.metric.metric_type_id, value: formatMetricSubmitValue(entry) })));
      }
      onSuccess();
    } catch { setError(t('metricForm.error.saveFailed')); }
    finally { submittingRef.current = false; setSubmitting(false); }
  }

  return <>
    <dialog ref={dialog} className="metric-form modal-surface" aria-labelledby="metric-form-title" onCancel={event => { event.preventDefault(); close(); }}>
      <header className="metric-form__header">
        <div><p className="metric-form__date">{displayDigits(formatIsoDateLabel(date, i18n.language), i18n.language)}</p><h2 id="metric-form-title">{title}</h2><p>{t('metricForm.hint')}</p></div>
        <NmButton variant="ghost" type="button" onClick={close} disabled={submitting} aria-label={t('metricForm.action.close')}><IoCloseOutline aria-hidden /></NmButton>
      </header>
      <div className="metric-form__tools">
        {!isEdit && <NmButton type="button" variant="neutral" disabled={submitting} onClick={() => setHealthDocOpen(true)} icon={<IoDocumentTextOutline aria-hidden />}>{t('healthDocument.action.open')}</NmButton>}
        <NmButton type="button" variant="ghost" disabled={submitting} onClick={() => void leave(() => navigate('/settings/metrics'))} icon={<IoSettingsOutline aria-hidden />}>{t('metricForm.action.manage')}</NmButton>
      </div>
      <form onSubmit={submit} className="metric-form__form">
        <div className="metric-form__body" aria-busy={loading}>
          {loading && <p role="status"><NmLoader size="sm" inline label={t('metricForm.state.loading')} /></p>}
          {!loading && !entries.length && !error && <p>{t('metricForm.state.noMetrics')}</p>}
          {!loading && entries.map((entry, index) => {
            const items = entry.metric.metricTypeItems ?? entry.metric.metric_type_items ?? [];
            const label = presentHealthMetricName(entry.metric.name, i18n.language);
            const id = `metric-entry-${entry.metric.metric_type_id}`;
            const invalid = invalidIds.includes(entry.metric.metric_type_id);
            return <div className="metric-form__field" key={id}>
              {items.length ? <><label htmlFor={id}>{label}</label><select id={id} className="nm-input-field" value={entry.selectedItemId} onChange={e => update(index, e.target.value)} disabled={submitting} aria-invalid={invalid}><option value="">{t('metricForm.state.optional')}</option>{items.map(item => <option key={item.id} value={item.id}>{i18n.language.startsWith('fa') ? item.fa_name || item.en_name : item.en_name || item.fa_name}</option>)}</select></> :
                <NmInput id={id} label={label} type="text" inputMode={entry.metric.data_type === 'text' ? 'text' : 'decimal'} dir="ltr" autoComplete="off" value={entry.value} onChange={e => update(index, e.target.value)} placeholder={t('metricForm.state.optional')} disabled={submitting} aria-invalid={invalid} error={invalid ? t('metricForm.error.invalid') : undefined} />}
              {entry.metric.unit && <span className="metric-form__unit">{entry.metric.unit}</span>}
            </div>;
          })}
          {error && <div className="metric-form__error" role="alert">{error}{!entries.length && <NmButton type="button" onClick={() => setRetry(value => value + 1)} disabled={loading}>{t('common.retry')}</NmButton>}</div>}
        </div>
        <footer className="metric-form__footer">
          <span role="status">{filled.length ? displayDigits(t('metricForm.state.filled', { count: filled.length }), i18n.language) : t('metricForm.state.fillAtLeastOne')}</span>
          <div><NmButton type="submit" variant="primary" loading={submitting} disabled={loading || !filled.length}>{submitting ? t('metricForm.action.saving') : t('metricForm.action.save')}</NmButton><NmButton type="button" onClick={close} disabled={submitting}>{t('metricForm.action.cancel')}</NmButton></div>
        </footer>
      </form>
    </dialog>
    <HealthDocumentUploadSheet open={healthDocOpen} onClose={() => setHealthDocOpen(false)} userId={userId} testDateHint={date} onSuccess={onSuccess} />
  </>;
}
